import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { addRemoveError, getControl } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { toNum } from '@shared/helpers/math.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { formPaths } from '@shared/constants/form-paths.const';
import { getNatMutControl, getNatMutGroup, getSchedaPrevalenzaControl, getSchedaPrevalenzaGroup, natMutCkHelper } from './helpers/natura-mutualistica-form-validators.helper';
import { conferimentoProdottiInvalidMessage, conferimentoServiziInvalidMessage, occasioniLavoroInvalidMessage, offertaBeniInvalidMessage } from '../common/consts/validation-message.const';

export const naturaMutualisticaScopoRules: FormGroupRules = {
    my_CkOffreBeniServCoopAttPrimoAnno: (form: FormGroup) => {
        scopoMutualisticoCkHelper(form, 'my_CkOffreBeniServCoopAttPrimoAnno', 'my_CkOffreBeniServ', `${formPaths.datiBilancio}.my_BilAnno1`, offertaBeniInvalidMessage);
    },

    my_CkOffreBeniServCoopAtt: (form: FormGroup) => {
        scopoMutualisticoCkHelper(form, 'my_CkOffreBeniServCoopAtt', 'my_CkOffreBeniServ', `${formPaths.datiBilancio}.my_BilAnno2`, offertaBeniInvalidMessage);
    },

    my_CkOffreOccLavSociCoopAttPrimoAnno: (form: FormGroup) => {
        scopoMutualisticoCkHelper(form, 'my_CkOffreOccLavSociCoopAttPrimoAnno', 'my_CkOffreOccLavSoci', `${formPaths.datiBilancio}.my_BilAnno1`, occasioniLavoroInvalidMessage);
    },

    my_CkOffreOccLavSociCoopAtt: (form: FormGroup) => {
        scopoMutualisticoCkHelper(form, 'my_CkOffreOccLavSociCoopAtt', 'my_CkOffreOccLavSoci', `${formPaths.datiBilancio}.my_BilAnno2`, occasioniLavoroInvalidMessage);
    },

    my_CkRemunConferProdSociCoopAttPrimoAnno: (form: FormGroup) => {
        scopoMutualisticoCkHelper(form, 'my_CkRemunConferProdSociCoopAttPrimoAnno', 'my_CkRemunConferProdSoci', `${formPaths.datiBilancio}.my_BilAnno1`, conferimentoProdottiInvalidMessage);
    },

    my_CkRemunConferProdSociCoopAtt: (form: FormGroup) => {
        scopoMutualisticoCkHelper(form, 'my_CkRemunConferProdSociCoopAtt', 'my_CkRemunConferProdSoci', `${formPaths.datiBilancio}.my_BilAnno2`, conferimentoProdottiInvalidMessage);
    },

    my_CkRemunPrestConfServSociCoopAttPrimoAnno: (form: FormGroup) => {
        scopoMutualisticoCkHelper(form, 'my_CkRemunPrestConfServSociCoopAttPrimoAnno', 'my_CkRemunPrestConfServSoci', `${formPaths.datiBilancio}.my_BilAnno1`, conferimentoServiziInvalidMessage);
    },

    my_CkRemunPrestConfServSociCoopAtt: (form: FormGroup) => {
        scopoMutualisticoCkHelper(form, 'my_CkRemunPrestConfServSociCoopAtt', 'my_CkRemunPrestConfServSoci', `${formPaths.datiBilancio}.my_BilAnno2`, conferimentoServiziInvalidMessage);
    },

    my_CkOffreBeniServ: (form: FormGroup) => {
        const my_CkOffreBeniServControl = getNatMutControl(form, 'my_CkOffreBeniServ');
        const errors = my_CkOffreBeniServControl.errors ? my_CkOffreBeniServControl.errors : {};

        addRemoveError(isNilOrEmpty(my_CkOffreBeniServControl.value), errors, 'my_CkOffreBeniServ', {
            status: REQUIRED_STATUS,
        });

        my_CkOffreBeniServControl.setErrors(isEqual(errors, {}) ? null : errors);
        scopoMutualisticoIncoerente(form, 'my_CkOffreBeniServ');
    },

    my_CkOffreOccLavSoci: (form: FormGroup) => {
        scopoMutualisticoIncoerente(form, 'my_CkOffreOccLavSoci');
    },

    my_CkRemunConferProdSoci: (form: FormGroup) => {
        scopoMutualisticoIncoerente(form, 'my_CkRemunConferProdSoci');
    },

    my_CkRemunPrestConfServSoci: (form: FormGroup) => {
        scopoMutualisticoIncoerente(form, 'my_CkRemunPrestConfServSoci');
    },
};

/** Valida le checkbox "coopAtt" della scheda prevalenza rispetto al ck scopo e all'anno bilancio */
const scopoMutualisticoCkHelper = (form: FormGroup, targetPath: string, ckPath: string, annoPath: string, noResInvalidMessage: string): void => {
    const naturaMutualisticaGroup = getNatMutGroup(form);
    const schedaPrevalenzaGroup = getSchedaPrevalenzaGroup(form);
    const targetControl = getControl(schedaPrevalenzaGroup, targetPath);
    const my_CkOffreBeniServ = toNum(getControl(naturaMutualisticaGroup, ckPath)?.value);
    const my_BilAnno = toNum(getControl(form, annoPath)?.value);
    const errors = targetControl.errors ? targetControl.errors : {};
    const showRequiredError = isNilOrEmpty(targetControl?.value) && !isNilOrEmpty(my_BilAnno) && my_CkOffreBeniServ === 1;
    const showRequiredNoResError = my_CkOffreBeniServ === 0 && toNum(targetControl?.value) === 1;

    addRemoveError(showRequiredNoResError, errors, targetPath, {
        status: 'Obbligatorio rispondere "No"',
        message: noResInvalidMessage,
    });

    addRemoveError(showRequiredError, errors, targetPath, {
        status: REQUIRED_STATUS,
    });

    targetControl.setErrors(isEqual(errors, {}) ? null : errors);
};

/** Aggiunge errore "incoerente" se CkScopoMutual=1 ma nessun tipo di scambio mutualistico è attivo */
const scopoMutualisticoIncoerente = (form: FormGroup, targetPath: string): void => {
    const naturaMutualisticaGroup = getNatMutGroup(form);
    const targetControl = getControl(naturaMutualisticaGroup, targetPath);
    const my_CkScopoMutual = toNum(getControl(naturaMutualisticaGroup, 'my_CkScopoMutual')?.value);
    const my_CkOffreBeniServ = toNum(getControl(naturaMutualisticaGroup, 'my_CkOffreBeniServ')?.value);
    const my_CkOffreOccLavSoci = toNum(getControl(naturaMutualisticaGroup, 'my_CkOffreOccLavSoci')?.value);
    const my_CkRemunConferProdSoci = toNum(getControl(naturaMutualisticaGroup, 'my_CkRemunConferProdSoci')?.value);
    const my_CkRemunPrestConfServSoci = toNum(getControl(naturaMutualisticaGroup, 'my_CkRemunPrestConfServSoci')?.value);
    const errors = targetControl.errors ? targetControl.errors : {};
    const showError = my_CkScopoMutual === 1 && my_CkOffreBeniServ === 0 && my_CkOffreOccLavSoci === 0 && my_CkRemunConferProdSoci === 0 && my_CkRemunPrestConfServSoci === 0;

    addRemoveError(showError, errors, targetPath, {
        status: 'Risposta incoerente',
        message: 'Verificare lo scopo mutualistico',
    });

    targetControl.setErrors(isEqual(errors, {}) ? null : errors);
};
